import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  OutlinedInput,
  IconButton,
  InputAdornment,
  Tooltip,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
  Stack,
  Autocomplete,
  TextField,
  useTheme,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Select,
} from "@mui/material";
import { Search, SignalCellularNullTwoTone } from "@mui/icons-material";
import { ZoomIn as ViewIcon, Edit as EditIcon } from "@mui/icons-material";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";

import Header from "component/Header";
import BulkApproveDialog from "component/BulkApproveDialog";
import { engineerReportsById, readAsyncStorageValues, getAllOldReports, bulkApproveCompletedTickets } from "features/service/service.slice";
import Service from "features/service/service";
import "../../App.css";
import "../../stylesheet/engReports.css";
import DataGridTable from "component/DataGridTable";
import TableSkeleton from "component/Loaders/TableSkeleton";
import { Links } from "utils/pageLinkNames.js";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

const Reports = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [engineerId, setEngineerId] = React.useState("");
  const dispatch = useDispatch();
  const [serviceTickets, setServiceTickets] = useState([]);
  const [date, setDate] = useState(null);

  const { engineerReports, appConstants, allOldReports, isLoading, bulkApproval } = useSelector((state) => state.service);
  const { mode } = useSelector((state) => state.globalState);
  const { user } = useSelector((state) => state.auth);
  const [selectedTab, setSelectedTab] = useState(1);
  const [showBulkApproveDialog, setShowBulkApproveDialog] = useState(false);

  const [tickets90Days, setTickets90Days] = useState([]);
  const [ticketsMoreThan90Days, setTicketsMoreThan90Days] = useState([]);

  const [filterOptions, setFilterOptions] = useState({
    status: [],
    maintenanceCode: [],
    category: [],
    model: [],
  });

  const engineerReportsData = async (userId) => {
    dispatch(engineerReportsById(userId));
  };

  const onChange = (event) => {
    const value = event.target.value;
    setEngineerId(value);
    engineerReportsData(value);
  };

  useEffect(() => {
    dispatch(engineerReportsById());
  }, []);

  useEffect(() => {
    dispatch(getAllOldReports());
  }, []);

  useEffect(() => {
    const today = new Date();
    const ninetyDaysAgo = new Date(today);
    ninetyDaysAgo.setDate(today.getDate() - 90);

    if (engineerReports?.length) {
      const tickets90 = engineerReports
        .filter((ticket) => {
          const completedDate = new Date(ticket.dateCompleted);
          return completedDate >= ninetyDaysAgo;
        })
        .map((ticket) => ({
          ...ticket,
          svcTktNumber: appConstants?.showServiceTicketId ? ticket.tktId : ticket.svcTktNumber || ticket.tktId,
          dateCreated: moment(ticket.dateCreated).format("DD MMM YYYY"),
          dateCompleted: moment(ticket.dateCompleted).format("DD MMM YYYY"),
        }))
        .sort((a, b) => b.ticketId - a.ticketId);

      setTickets90Days(tickets90);
    }
  }, [engineerReports, appConstants?.showServiceTicketId]);

  useEffect(() => {
    if (allOldReports?.data?.length) {
      const ticketsMoreThan90Days = allOldReports.data
        .filter((ticket) => {
          const completedDate = new Date(ticket.dateCompleted);
          return completedDate;
        })
        .map((ticket) => ({
          ...ticket,
          svcTktNumber: appConstants?.showServiceTicketId ? ticket.tktId : ticket.svcTktNumber || ticket.tktId,
          dateCreated: moment(ticket.dateCreated).format("DD MMM YYYY"),
          dateCompleted: moment(ticket.dateCompleted).format("DD MMM YYYY"),
        }))
        .sort((a, b) => b.ticketId - a.ticketId);

      setTicketsMoreThan90Days(ticketsMoreThan90Days);
    }
  }, [allOldReports, appConstants?.showServiceTicketId]);

  const [searchValue, setSearchValue] = useState("");
  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const gridReportsAction = (args) => {
    return (
      <Link to={`/${Links.SERVICE_TICKET}/` + args.row.tktId}>
        <IconButton sx={{ mr: 1 }}>
          <Tooltip title="View">
            <ViewIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
          </Tooltip>
        </IconButton>
      </Link>
    );
  };
  const dateComparator = (v1, v2) => moment(v1).diff(moment(v2));

  const maintenanceReport = async () => {
    navigate(`${Links.MAINTENANCE_REPORT}`, {
      state: { date: date ? moment(date).format("YYYY-MM-DD") : moment(new Date()).format("YYYY-MM-DD") },
    });
  };

  const handleTabsChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const clearAllFilters = () => {
    setFilterOptions({
      status: [],
      maintenanceCode: [],
      category: [],
      model: [],
    });
    setSearchValue("");
  };

  const handleBulkApproveClick = () => {
    setShowBulkApproveDialog(true);
  };

  const handleCloseBulkApproveDialog = () => {
    setShowBulkApproveDialog(false);
  };

  const getCurrentTabData = () => {
    return selectedTab === 1 ? tickets90Days : ticketsMoreThan90Days;
  };

  const FilterDropdown = ({ label, value, options, onChange }) => {
    const sortedOptions = [...options].sort((a, b) => a.localeCompare(b));
    return (
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        displayEmpty
        style={{ width: "100%", fontSize: "0.875rem", fontWeight: value ? "normal" : 600 }}
        renderValue={(selected) => selected || <span style={{ fontWeight: 600, fontSize: "0.800rem" }}>{label}</span>}
      >
        <MenuItem value="" style={{ fontWeight: 600 }}>
          {label}
        </MenuItem>
        {sortedOptions.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    );
  };

  const getFilteredData = () => {
    const currentData = getCurrentTabData();

    return currentData.filter((ticket) => {
      if (searchValue.trim() !== "") {
        const searchLower = searchValue.toLowerCase();
        const searchMatch = Object.values(ticket).join(" ").toLowerCase().includes(searchLower);
        if (!searchMatch) return false;
      }

      if (filterOptions.status?.length && !filterOptions.status.includes(ticket.ticketStatus)) {
        return false;
      }

      if (filterOptions.maintenanceCode?.length && !filterOptions.maintenanceCode.includes(ticket.maintenanceCode)) {
        return false;
      }

      if (filterOptions.category?.length && !filterOptions.category.includes(ticket.ticketCategory)) {
        return false;
      }

      if (filterOptions.model?.length && !filterOptions.model.includes(ticket.machineModel)) {
        return false;
      }

      return true;
    });
  };

  const reportGrid = [
    {
      field: "svcTktNumber",
      headerName: "Ticket ID",
      minWidth: 30,
      flex: 0.2,
      valueGetter: (params) => {
        return appConstants?.showServiceTicketId ? params.row.tktId : params.row.svcTktNumber;
      },
      renderCell: (params) => {
        const displayValue = appConstants?.showServiceTicketId ? params.row.tktId : params.value;
        return (
          <span
            style={{
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => navigate(`/${Links.SERVICE_TICKET}/` + params.row.tktId)}
          >
            {displayValue}
          </span>
        );
      },
    },
    {
      field: "dateCreated",
      headerName: "Created",
      minWidth: 50,
      flex: 0.3,
      sortComparator: dateComparator,
    },
    {
      field: "dateCompleted",
      headerName: "Completed",
      minWidth: 50,
      flex: 0.3,
      sortComparator: dateComparator,
    },
    {
      field: "maintenanceCode",
      headerName: "Maintenance code",
      minWidth: 60,
      flex: 0.3,
      renderHeader: () => {
        const currentData = getCurrentTabData();
        const maintenanceCodes = [...new Set(currentData.map((t) => t.maintenanceCode).filter(Boolean))];

        return (
          <FilterDropdown
            label="Maintenance code"
            value={filterOptions.maintenanceCode?.[0] || ""}
            options={maintenanceCodes}
            onChange={(selected) => {
              const selectedArray = selected ? [selected] : [];
              setFilterOptions((prev) => ({ ...prev, maintenanceCode: selectedArray }));
            }}
          />
        );
      },
    },
    {
      field: "serialNo",
      headerName: "Serial Number",
      minWidth: 60,
      flex: 0.3,
    },
    {
      field: "machineModel",
      headerName: "Model",
      minWidth: 60,
      flex: 0.3,
      renderHeader: () => {
        const currentData = getCurrentTabData();
        const models = [...new Set(currentData.map((t) => t.machineModel).filter(Boolean))];

        return (
          <FilterDropdown
            label="Model"
            value={filterOptions.model?.[0] || ""}
            options={models}
            onChange={(selected) => {
              const selectedArray = selected ? [selected] : [];
              setFilterOptions((prev) => ({ ...prev, model: selectedArray }));
            }}
          />
        );
      },
    },
    {
      field: "ticketCategory",
      headerName: "Category",
      minWidth: 60,
      flex: 0.3,
      renderHeader: () => {
        const currentData = getCurrentTabData();
        const categories = [...new Set(currentData.map((t) => t.ticketCategory).filter(Boolean))];

        return (
          <FilterDropdown
            label="Category"
            value={filterOptions.category?.[0] || ""}
            options={categories}
            onChange={(selected) => {
              const selectedArray = selected ? [selected] : [];
              setFilterOptions((prev) => ({ ...prev, category: selectedArray }));
            }}
          />
        );
      },
    },
    {
      field: "ticketStatus",
      headerName: "Status",
      minWidth: 60,
      flex: 0.3,
      // {
      //   headerName: "",
      //   minWidth: 60,
      //   flex: 0.35,
      //   sortable: false,
      //   filterable: false,
      //   renderCell: gridReportsAction,
      // },
      renderHeader: () => {
        const currentData = getCurrentTabData();
        const statuses = [...new Set(currentData.map((t) => t.ticketStatus).filter(Boolean))];

        return (
          <FilterDropdown
            label="Status"
            value={filterOptions.status?.[0] || ""}
            options={statuses}
            onChange={(selected) => {
              const selectedArray = selected ? [selected] : [];
              setFilterOptions((prev) => ({ ...prev, status: selectedArray }));
            }}
          />
        );
      },
    },
  ];

  return (
    <div>
      <Stack sx={{ ml: 2, mt: 2, mb: 1 }}>
        <Header titles={["Reports"]} />
      </Stack>
      <Box sx={{ mx: 2 }}>
        <Stack direction={"row"} gap={1} alignItems={"center"} my={2}>
          <OutlinedInput
            id="outlined-adornment-password"
            value={searchValue}
            onChange={(event) => handleSearchChange(event)}
            placeholder="Search..."
            endAdornment={
              <InputAdornment position="end">
                <IconButton aria-label="toggle password visibility" edge="end">
                  <Search />
                </IconButton>
              </InputAdornment>
            }
            sx={{ minWidth: 300, maxWidth: 600, height: 35 }}
          />
          <Autocomplete
            onChange={onChange}
            size="small"
            value={engineerId ? appConstants?.appUsers.find((user) => user.userId === engineerId) : null}
            options={
              Object.keys(appConstants).length
                ? appConstants?.appUsers.filter((obj) => {
                  return obj.userRole === appConstants.allTypes.USER_ROLES.ROLE_SERVICE_TECHNICIAN;
                })
                : []
            }
            getOptionLabel={(option) => option.nameEmpno}
            renderInput={(params) => <TextField {...params} label="Select technician" />}
            sx={{ width: 300 }}
            renderOption={(props, option) => (
              <MenuItem {...props} key={option.userId} value={option.userId}>
                {option.nameEmpno}
              </MenuItem>
            )}
          />

          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              maxDate={moment()}
              label="Select date"
              format={"DD MMM YYYY"}
              value={date ? moment(date) : null}
              onChange={(newValue) => setDate(newValue)}
              slotProps={{ textField: { size: "small" } }}
            />
          </LocalizationProvider>
          <Button
            // startIcon={emailSending ? <CircularProgress size={12} /> : <SendIcon />}
            sx={{ height: "self", textTransform: "none" }}
            variant="contained"
            onClick={() => maintenanceReport()}
          >
            Maintenance Report
          </Button>
          <Button
            startIcon={<CloseIcon />}
            sx={{ height: "self" }}
            autoCapitalize="none"
            variant="contained"
            onClick={clearAllFilters}
          >
            Clear All
          </Button>
          {user?.roles?.includes(appConstants?.allTypes?.USER_ROLES?.ROLE_MAINTENANCE_SUPERVISOR) && (
            <Button
              variant="contained"
              onClick={handleBulkApproveClick}
              disabled={bulkApproval?.isLoading}
              sx={{ textTransform: "none" }}
            >
              {bulkApproval?.isLoading ? "Processing..." : "Bulk Approve"}
            </Button>
          )}
        </Stack>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={selectedTab} onChange={handleTabsChange} aria-label="service tickets tabs" centered>
            <Tab
              sx={{
                fontWeight: "bold",
                fontSize: "0.9rem",
                marginRight: "150px",
                "&:hover": {
                  backgroundColor: "initial",
                  color: "initial",
                },
              }}
              label={`60 Days (${tickets90Days.length})`}
              value={1}
            />
            <Tab
              sx={{
                fontWeight: "bold",
                fontSize: "0.9rem",
                marginRight: "120px",
                "&:hover": {
                  backgroundColor: "initial",
                  color: "initial",
                },
              }}
              label={`More Than 60 Days (${ticketsMoreThan90Days.length})`}
              value={2}
            />
          </Tabs>
        </Box>
        {isLoading ? (
          <TableSkeleton rows={8} columns={reportGrid.length} />
        ) : (
          <DataGridTable
            rowId={"tktId"}
            data={getFilteredData()}
            columnAttributes={reportGrid}
          />
        )}
      </Box>

      <BulkApproveDialog
        open={showBulkApproveDialog}
        onClose={handleCloseBulkApproveDialog}
        loading={bulkApproval?.isLoading}
        onConfirm={(days) => {
          dispatch(bulkApproveCompletedTickets(days)).then((res) => {
            if (!res.error) {
              toast.success(res?.payload?.message || res.payload || "Bulk approval started. You can continue working.");
              handleCloseBulkApproveDialog();
            } else {
              toast.error(res?.payload?.message || res.payload || "Bulk approval failed.");
            }
          });
        }}
      />
    </div>
  );
};
export default Reports;